import Fuse from "fuse.js";
import type { CategoryDto, DocumentDto, UploadMeta } from "@piano/shared";
import { api } from "../api/index.js";
import { splitTitle } from "../format.js";
import { isString, readPref, writePref } from "../persisted.js";
import { ui } from "./ui.svelte.js";

export interface Piece extends DocumentDto {
  name: string;
  artist: string;
  thumb: string;
}

/** Filter: „all“, „fav“, „recent“, „shared“ oder „cat:<id>“ */
export type LibraryFilter = string;

function toPiece(doc: DocumentDto): Piece {
  const { title, artist } = splitTitle(doc.title);
  return { ...doc, name: title, artist, thumb: api.thumbnailUrl(doc) };
}

class Library {
  pieces = $state<Piece[]>([]);
  categories = $state<CategoryDto[]>([]);
  loaded = $state(false);
  query = $state("");
  filter = $state<LibraryFilter>("all");
  private userId = "";

  private fuse = $derived(
    new Fuse(this.pieces, {
      keys: [
        { name: "name", weight: 3 },
        { name: "artist", weight: 2 },
        { name: "fileName", weight: 1 },
      ],
      threshold: 0.32,
      ignoreLocation: true,
    }),
  );

  byId = $derived(new Map(this.pieces.map((piece) => [piece.id, piece])));
  categoryById = $derived(new Map(this.categories.map((category) => [category.id, category])));

  favorites = $derived(this.pieces.filter((piece) => piece.favorite));
  recent = $derived(
    this.pieces
      .filter((piece) => piece.lastOpenedAt)
      .sort((a, b) => (b.lastOpenedAt ?? 0) - (a.lastOpenedAt ?? 0))
      .slice(0, 12),
  );
  sharedWithMe = $derived(this.pieces.filter((piece) => piece.accessRole === "shared"));

  /** Gefilterte und durchsuchte Stücke für die Trefferansicht. */
  results = $derived.by(() => {
    let list: Piece[];
    const q = this.query.trim();
    if (q) list = this.fuse.search(q).map((hit) => hit.item);
    else list = [...this.pieces].sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
    const f = this.filter;
    if (f === "fav") list = list.filter((p) => p.favorite);
    else if (f === "recent") {
      list = list.filter((p) => p.lastOpenedAt);
      if (!q) list.sort((a, b) => (b.lastOpenedAt ?? 0) - (a.lastOpenedAt ?? 0));
    } else if (f === "shared") list = list.filter((p) => p.accessRole === "shared");
    else if (f.startsWith("cat:")) list = list.filter((p) => p.categoryIds.includes(f.slice(4)));
    return list;
  });

  searching = $derived(this.query.trim() !== "" || this.filter !== "all");

  init(userId: string): void {
    this.userId = userId;
    this.filter = readPref(userId, "library.filter", "all", isString);
    this.query = "";
  }

  setFilter(filter: LibraryFilter): void {
    this.filter = filter;
    writePref(this.userId, "library.filter", filter);
  }

  async load(): Promise<void> {
    const [documents, categories] = await Promise.all([api.documents(), api.categories()]);
    this.pieces = documents.map(toPiece);
    this.categories = categories;
    this.loaded = true;
    if (this.filter.startsWith("cat:") && !this.categoryById.has(this.filter.slice(4))) this.setFilter("all");
  }

  async loadCategories(): Promise<void> {
    this.categories = await api.categories();
  }

  private patch(id: string, change: Partial<Piece>): void {
    this.pieces = this.pieces.map((piece) => (piece.id === id ? { ...piece, ...change } : piece));
  }

  async toggleFavorite(id: string): Promise<void> {
    const piece = this.byId.get(id);
    if (!piece) return;
    const next = !piece.favorite;
    this.patch(id, { favorite: next });
    try {
      await api.setFavorite(id, next);
      ui.toast(next ? "Zu Favoriten hinzugefügt" : "Aus Favoriten entfernt", "success");
    } catch (error) {
      this.patch(id, { favorite: !next });
      ui.error(error);
    }
  }

  async setCategories(id: string, categoryIds: string[]): Promise<void> {
    const before = this.byId.get(id)?.categoryIds ?? [];
    this.patch(id, { categoryIds });
    try {
      await api.setDocumentCategories(id, categoryIds);
    } catch (error) {
      this.patch(id, { categoryIds: before });
      ui.error(error);
    }
  }

  setSecsPerPage(id: string, secsPerPage: number | null): void {
    this.patch(id, { secsPerPage });
  }

  markOpened(id: string): void {
    this.patch(id, { lastOpenedAt: Date.now() });
    void api.markOpened(id).catch(() => undefined);
  }

  async remove(id: string, force = false): Promise<void> {
    await api.deleteDocument(id, force);
    this.pieces = this.pieces.filter((piece) => piece.id !== id);
  }

  async leave(id: string): Promise<void> {
    await api.leaveShare(id);
    this.pieces = this.pieces.filter((piece) => piece.id !== id);
  }

  async upload(file: File, meta: UploadMeta, onProgress: (fraction: number) => void): Promise<Piece> {
    const doc = toPiece(await api.upload(file, meta, onProgress));
    this.pieces = [...this.pieces.filter((piece) => piece.id !== doc.id), doc];
    return doc;
  }

  async createCategory(input: { name: string; color?: string; icon?: string }): Promise<CategoryDto> {
    const category = await api.createCategory(input);
    this.categories = [...this.categories, category];
    return category;
  }

  async updateCategory(id: string, input: { name?: string; color?: string; icon?: string }): Promise<void> {
    const category = await api.updateCategory(id, input);
    this.categories = this.categories.map((c) => (c.id === id ? category : c));
  }

  async deleteCategory(id: string): Promise<void> {
    await api.deleteCategory(id);
    this.categories = this.categories.filter((c) => c.id !== id);
    this.pieces = this.pieces.map((p) =>
      p.categoryIds.includes(id) ? { ...p, categoryIds: p.categoryIds.filter((c) => c !== id) } : p,
    );
    if (this.filter === `cat:${id}`) this.setFilter("all");
  }

  reset(): void {
    this.pieces = [];
    this.categories = [];
    this.loaded = false;
  }
}

export const library = new Library();
